import { generateClient, isCancelError, post } from "aws-amplify/api";

const client = generateClient();

export async function requestListBusinessFixedAsset(dispatch, payload) {
  try {
    const { page, businessID, limit = 10, nextTokens } = payload;

    dispatch({
      type: "REQUEST_LIST_FIXED_ASSET",
      payload: { request: null, page },
    });

    let items = [];
    let nextToken = nextTokens || "start";

    const params = {
      businessID,
      limit,
      sortDirection: "DESC",
    };

    while (nextToken && items.length === 0) {
      if (nextToken && nextToken !== "start") {
        params.nextToken = nextToken;
      }

      const request = client.graphql({
        query: fixedAssetsByBusinessCreatedAt,
        variables: params,
      });
      dispatch({
        type: "REQUEST_LIST_FIXED_ASSET",
        payload: { request, page },
      });

      // eslint-disable-next-line no-await-in-loop
      const res = await request;
      items = [...items, ...res.data.fixedAssetsByBusinessCreatedAt.items];
      nextToken = res.data.fixedAssetsByBusinessCreatedAt.nextToken;
    }

    const nextPage = page + 1;

    dispatch({
      type: "LIST_FIXED_ASSET_SUCCESS",
      payload: { items, nextToken, nextPage },
    });
  } catch (error) {
    if (!isCancelError(error)) {
      dispatch({ type: "LIST_FIXED_ASSET_ERROR", error });
    }
  }
}

export async function requestGetBusinessFixedAsset(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_GET_FIXED_ASSET", payload: { request: null } });
    const { id } = payload;

    const request = client.graphql({
      query: getBusinessFixedAsset,
      variables: { id },
    });
    dispatch({ type: "REQUEST_GET_FIXED_ASSET", payload: { request } });

    const res = await request;

    dispatch({
      type: "GET_FIXED_ASSET_SUCCESS",
      payload: { asset: res.data.getBusinessFixedAsset },
    });
  } catch (error) {
    dispatch({ type: "GET_FIXED_ASSET_ERROR", error });
  }
}

export async function requestCreateBusinessFixedAsset(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_CREATE_FIXED_ASSET",
      payload: { request: null },
    });
    const { input } = payload;

    const request = post({
      apiName: "accounting",
      path: "/fixed-asset",
      options: {
        body: {
          action: "create",
          ...input,
        },
      },
    });
    dispatch({ type: "REQUEST_CREATE_FIXED_ASSET", payload: { request } });

    const { body } = await request.response;
    const res = await body.json();

    if (res.success === false) {
      dispatch({
        type: "ERROR_CREATE_FIXED_ASSET",
        error: res.error || "Gagal membuat aset",
      });
      return;
    }

    dispatch({
      type: "SUCCESS_CREATE_FIXED_ASSET",
      payload: { asset: res.data },
    });
  } catch (error) {
    dispatch({ type: "ERROR_CREATE_FIXED_ASSET", error });
  }
}

export async function requestUpdateBusinessFixedAsset(dispatch, payload) {
  // Register edits go through the /fixed-asset Lambda so financial fields
  // (cost, life, dates, account codes) are rejected once depreciation has
  // posted; direct updateBusinessFixedAsset mutations are no longer allowed.
  try {
    dispatch({
      type: "REQUEST_UPDATE_FIXED_ASSET",
      payload: { request: null },
    });
    const { input } = payload;
    const { id, businessID, ...fields } = input;

    const request = post({
      apiName: "accounting",
      path: "/fixed-asset",
      options: {
        body: {
          action: "update",
          businessID,
          assetID: id,
          ...fields,
        },
      },
    });
    dispatch({ type: "REQUEST_UPDATE_FIXED_ASSET", payload: { request } });

    const { body } = await request.response;
    const res = await body.json();

    if (res.success === false) {
      dispatch({
        type: "ERROR_UPDATE_FIXED_ASSET",
        error: res.error || "Gagal memperbarui aset",
      });
      return;
    }

    dispatch({
      type: "UPDATE_FIXED_ASSET_SUCCESS",
      payload: { asset: res.data },
    });
  } catch (error) {
    dispatch({ type: "ERROR_UPDATE_FIXED_ASSET", error });
  }
}

export async function requestDeleteBusinessFixedAsset(dispatch, payload) {
  // Deletes go through the /fixed-asset Lambda, which refuses assets with
  // posted depreciation or a disposal (audit trail preserved).
  try {
    dispatch({
      type: "REQUEST_DELETE_FIXED_ASSET",
      payload: { request: null },
    });
    const { id, businessID } = payload;

    const request = post({
      apiName: "accounting",
      path: "/fixed-asset",
      options: {
        body: {
          action: "delete",
          businessID,
          assetID: id,
        },
      },
    });
    dispatch({ type: "REQUEST_DELETE_FIXED_ASSET", payload: { request } });

    const { body } = await request.response;
    const res = await body.json();

    if (res.success === false) {
      dispatch({
        type: "DELETE_FIXED_ASSET_ERROR",
        error: res.error || "Gagal menghapus aset",
      });
      return;
    }

    dispatch({
      type: "DELETE_FIXED_ASSET_SUCCESS",
      payload: { id: res.data.assetID },
    });
  } catch (error) {
    dispatch({ type: "DELETE_FIXED_ASSET_ERROR", error });
  }
}

export async function requestListFixedAssetDepreciation(dispatch, payload) {
  try {
    const { assetID, limit = 100 } = payload;

    dispatch({
      type: "REQUEST_LIST_DEPRECIATION",
      payload: { request: null },
    });

    const request = client.graphql({
      query: depreciationByAssetPeriod,
      variables: { assetID, sortDirection: "DESC", limit },
    });
    dispatch({ type: "REQUEST_LIST_DEPRECIATION", payload: { request } });

    const res = await request;
    dispatch({
      type: "LIST_DEPRECIATION_SUCCESS",
      payload: { items: res.data.depreciationByAssetPeriod.items },
    });
  } catch (error) {
    if (!isCancelError(error)) {
      dispatch({ type: "LIST_DEPRECIATION_ERROR", error });
    }
  }
}

export async function requestRunDepreciation(dispatch, payload) {
  // PSAK 16 straight-line depreciation posting for every active asset up to the
  // given period end. Server-side double-entry via the /fixed-asset action.
  const { businessID, periodEnd } = payload;

  dispatch({ type: "REQUEST_RUN_DEPRECIATION", payload: {} });

  try {
    const request = post({
      apiName: "accounting",
      path: "/fixed-asset",
      options: {
        body: {
          action: "run_depreciation",
          business_id: businessID,
          period_end: periodEnd,
        },
      },
    });

    const { body } = await request.response;
    const res = await body.json();

    // A refusal comes back 200 with success:false; without this the page would
    // dispatch an undefined result and the modal would just stop spinning.
    if (res.success === false) {
      dispatch({
        type: "RUN_DEPRECIATION_ERROR",
        error: res.error || "Gagal menjalankan penyusutan",
      });
      return;
    }

    dispatch({
      type: "RUN_DEPRECIATION_SUCCESS",
      payload: { result: res.data },
    });
    return res.data;
  } catch (error) {
    if (!isCancelError(error)) {
      dispatch({ type: "RUN_DEPRECIATION_ERROR", error });
    }
  }
}

export async function requestDisposeFixedAsset(dispatch, payload) {
  // PSAK 16 disposal: removes cost + accumulated depreciation, books proceeds
  // and the gain/loss. Server-side double-entry via the /fixed-asset action.
  const { businessID, assetID, proceeds, disposalDate, cashAccountCode } =
    payload;

  dispatch({ type: "REQUEST_DISPOSE_FIXED_ASSET", payload: {} });

  try {
    const request = post({
      apiName: "accounting",
      path: "/fixed-asset",
      options: {
        body: {
          action: "dispose",
          business_id: businessID,
          asset_id: assetID,
          proceeds,
          disposal_date: disposalDate,
          cash_account_code: cashAccountCode,
        },
      },
    });

    const { body } = await request.response;
    const res = await body.json();

    if (res.success === false) {
      dispatch({
        type: "DISPOSE_FIXED_ASSET_ERROR",
        error: res.error || "Gagal melepas aset",
      });
      return null;
    }

    dispatch({
      type: "DISPOSE_FIXED_ASSET_SUCCESS",
      payload: { result: res.data },
    });
    return res.data;
  } catch (error) {
    if (!isCancelError(error)) {
      dispatch({ type: "DISPOSE_FIXED_ASSET_ERROR", error });
    }
  }
}

export async function clearBusinessFixedAsset(dispatch) {
  dispatch({ type: "CLEAR_FIXED_ASSET" });
}

export async function clearBusinessFixedAssetError(dispatch) {
  dispatch({ type: "CLEAR_FIXED_ASSET_ERROR" });
}

const fixedAssetFields = `
  id
  businessID
  name
  description
  assetAccountCode
  accumulatedAccountCode
  expenseAccountCode
  fundingAccountCode
  acquisitionTransactionID
  acquisitionCost
  residualValue
  usefulLifeMonths
  method
  acquisitionDate
  depreciationStartDate
  accumulatedDepreciation
  lastDepreciationDate
  status
  disposalDate
  disposalProceeds
  createdAt
  updatedAt
`;

const fixedAssetsByBusinessCreatedAt = /* GraphQL */ `
  query FixedAssetsByBusinessCreatedAt(
    $businessID: ID
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelBusinessFixedAssetFilterInput
    $limit: Int
    $nextToken: String
  ) {
    fixedAssetsByBusinessCreatedAt(
      businessID: $businessID
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {${fixedAssetFields}}
      nextToken
    }
  }
`;

const getBusinessFixedAsset = /* GraphQL */ `
  query GetBusinessFixedAsset($id: ID!) {
    getBusinessFixedAsset(id: $id) {${fixedAssetFields}}
  }
`;

const depreciationByAssetPeriod = /* GraphQL */ `
  query DepreciationByAssetPeriod(
    $assetID: ID
    $periodDate: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelFixedAssetDepreciationFilterInput
    $limit: Int
    $nextToken: String
  ) {
    depreciationByAssetPeriod(
      assetID: $assetID
      periodDate: $periodDate
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        assetID
        businessID
        periodDate
        amount
        accumulatedAfter
        transactionID
        createdAt
      }
      nextToken
    }
  }
`;
